import React from 'react';
import { FileText, FileSpreadsheet, FileImage, File as FileIcon, Download, Eye, AlertCircle, Trash2, Sparkles } from 'lucide-react';
import { DOCUMENT_TYPE_LABELS } from '../config/labels';
import type { DocumentItem } from '../api/cases';
import apiClient from '../api/client';
import { downloadDocumentUrl, downloadStrippedDocumentUrl } from '../api/review';
import { purgeDocument } from '../api/documents';

interface DocumentRowProps {
  doc: DocumentItem;
  caseId: string;
  onView: (doc: DocumentItem) => void;
  onPurged?: (docId: string) => void;
}

const STATUS_STYLES: Record<string, string> = {
  OK: 'bg-green-100 text-green-700 border-green-300',
  ERRORED: 'bg-red-100 text-red-700 border-red-300',
  NOT_VERIFIABLE: 'bg-amber-100 text-amber-700 border-amber-300',
};

function getFileIcon(mime: string | null | undefined) {
  const m = mime || '';
  if (m.includes('sheet') || m.includes('excel') || m.includes('csv')) return FileSpreadsheet;
  if (m.includes('image')) return FileImage;
  if (m.includes('pdf') || m.includes('text') || m.includes('word') || m.includes('html')) return FileText;
  return FileIcon;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentRow({ doc, caseId, onView, onPurged }: DocumentRowProps) {
  const Icon = getFileIcon(doc.mime_type);
  const status = (doc.processing_status || 'pending').toUpperCase();
  const statusLabel = status === 'NOT_VERIFIABLE' ? 'Not Verifiable' : status.charAt(0) + status.slice(1).toLowerCase();
  const purged = !!doc.is_purged;

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (purged) return;
    try {
      const url = downloadDocumentUrl(caseId, doc.id);
      const res = await apiClient.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.original_filename || `${doc.id}-download`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Document download failed:', err);
      alert('Failed to download document.');
    }
  };

  const handleDownloadStripped = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!doc.has_pii_stripped_text) return;
    try {
      const url = downloadStrippedDocumentUrl(caseId, doc.id);
      const res = await apiClient.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      const base = doc.original_filename || `${doc.id}-washed`;
      const stem = base.includes('.') ? base.split('.').slice(0, -1).join('.') : base;
      a.download = `${stem}_DATAWASHED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Stripped document download failed:', err);
      alert('Failed to download data-washed copy.');
    }
  };

  const handlePurge = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (purged) return;
    if (!window.confirm(
      `Permanently delete the original file for "${doc.original_filename}"?\n` +
      `The file bytes are removed from disk. Metadata + audit history are retained.\n` +
      `This cannot be undone.`
    )) return;
    try {
      await purgeDocument(doc.id);
      onPurged?.(doc.id);
    } catch (err) {
      console.error('Purge failed:', err);
      alert('Failed to purge document.');
    }
  };

  return (
    <tr className="border-b border-slate-100 transition-colors hover:bg-slate-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <Icon size={18} className="text-slate-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{doc.original_filename}</p>
            <p className="text-xs text-slate-400 font-mono truncate">{doc.id}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
        {DOCUMENT_TYPE_LABELS[doc.document_type || doc.classification || ''] || doc.document_type || doc.classification || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{formatSize(doc.file_size_bytes || 0)}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.OK || 'bg-slate-100 text-slate-600 border-slate-300'}`}>
          {status === 'ERRORED' && <AlertCircle size={12} className="mr-1" />}
          {statusLabel}
        </span>
        {purged && (
          <span className="inline-flex items-center rounded-full border bg-slate-200 text-slate-600 border-slate-300 px-2 py-0.5 text-xs font-medium ml-1">
            Purged
          </span>
        )}
        {doc.is_encrypted && !purged && (
          <span className="inline-flex items-center rounded-full border bg-blue-50 text-blue-700 border-blue-200 px-2 py-0.5 text-xs font-medium ml-1" title="Stored as ciphertext on disk (AES-GCM)">
            Encrypted
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {purged ? (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400 italic">
              Original purged — view/download disabled
            </span>
          ) : (
            <>
              <button
                onClick={() => onView(doc)}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <Eye size={15} />
                View
              </button>
              <a
                href="#"
                onClick={handleDownload}
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
              >
                <Download size={15} />
                Download
              </a>
              {doc.has_pii_stripped_text && (
                <a
                  href="#"
                  onClick={handleDownloadStripped}
                  className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                  title="Download the PII-stripped (data-washed) copy that the AI sees"
                >
                  <Sparkles size={15} />
                  Download washed
                </a>
              )}
            </>
          )}
          <button
            onClick={handlePurge}
            disabled={purged}
            className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium disabled:text-slate-300 disabled:cursor-not-allowed"
            title={purged ? 'Original already purged' : 'Delete original file from disk (metadata kept)'}
          >
            <Trash2 size={15} />
            {purged ? 'Purged' : 'Purge original'}
          </button>
        </div>
      </td>
    </tr>
  );
}